import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, ChevronDown, ChevronRight, ChevronLeft, Trash2, Zap,
  Check, LayoutGrid, Users
} from 'lucide-react';
import { useTaskContext } from '../../context/TaskContext';
import TaskModal from './TaskModal';
import { useEmployee } from '../../context/EmployeeContext';

// ─── Constants ────────────────────────────────────────────────────────────────
// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_TO_COL = {
  'To Do':       'todo',
  'Inprogress':  'inprogress',
  'Deployment':  'deployment',
  'ReadyForQA':  'readyforqa',
  'qa':          'readyforqa',
  'Inreview':    'qa',
  'Ready For Production': 'review',  
  'Completed':   'done',
  'Reopened':    'reopened',
  'Cancelled':   'cancelled',
};

const COL_TO_STATUS = {
  todo:        'To Do',
  inprogress:  'Inprogress',
  deployment:  'Deployment',
  readyforqa:  'ReadyForQA',
  qa:          'Inreview',           // FIX: was 'QA' (not recognized by STATUS_TO_COL, so
                                     // the card snapped back to OPEN). 'Inreview' is what
                                     // STATUS_TO_COL actually maps back to the 'qa' column.
  review:      'Ready For Production', // FIX: was 'ready for production' (case mismatch
                                        // with STATUS_TO_COL's 'Ready For Production' key).
  done:        'Completed',
  reopened:    'Reopened',
  cancelled:   'Cancelled',
};

const COLUMNS = [
  { id: 'todo',       title: 'OPEN',                  bgColor: 'bg-slate-50'   },
  { id: 'inprogress', title: 'IN PROGRESS',           bgColor: 'bg-blue-50'    },
  { id: 'deployment', title: 'PENDING PRE PROD DEP',  bgColor: 'bg-orange-50'  },
  { id: 'readyforqa', title: 'READY FOR QA',          bgColor: 'bg-cyan-50'    },
  { id: 'qa',         title: 'QA IN PROGRESS',        bgColor: 'bg-violet-50'  },
  { id: 'review',     title: 'READY FOR PRODUCTION',  bgColor: 'bg-amber-50'   },
  { id: 'done',       title: 'DONE',                  bgColor: 'bg-emerald-50' },

  { id: 'cancelled',  title: 'CANCELLED',             bgColor: 'bg-red-50'     },
];

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-purple-500', 'bg-cyan-500',   'bg-pink-500',
  'bg-orange-500', 'bg-teal-500', 'bg-indigo-500', 'bg-rose-500',
  'bg-amber-500',  'bg-lime-500',
];

const PRIORITY_COLORS = {
  high:   'text-red-500',
  medium: 'text-yellow-500',
  low:    'text-blue-400',
  urgent: 'text-gray-400',
};

const TASK_ICON_CFG = {
  story:   { bg: 'bg-green-500', type: 'path', d: 'M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z' },
  bug:     { bg: 'bg-red-500',   type: 'circle' },
  task:    { bg: 'bg-blue-500',  type: 'path',   d: 'M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z' },
  subtask: { bg: 'bg-blue-400',  type: 'rect' },
};

// ─── Pure helpers ──────────────────────────────────────────────────────────────

const getAvatarColor = (id) => AVATAR_COLORS[(id ?? 0) % AVATAR_COLORS.length];

const getInitials = (emp) =>
  emp ? ((emp.first_name?.[0] ?? '') + (emp.last_name?.[0] ?? '')).toUpperCase() : 'U';

const normaliseTask = (task) => ({
  ...task,
  completed: task.status === 'Done',
  priority:  task.priority?.toLowerCase() ?? 'medium',
  type:      Array.isArray(task.type) ? 'task' : (task.type ?? 'task'),
});

const groupByColumn = (taskList) =>
  COLUMNS.reduce((acc, col) => {
    acc[col.id] = taskList.filter((t) => (STATUS_TO_COL[t.status] ?? 'todo') === col.id);
    return acc;
  }, {});

const sumPoints = (taskList) =>
  taskList.reduce((sum, t) => sum + (parseFloat(t.points) || 0), 0);

const buildColumns = (taskList) => {
const grouped = { todo: [], inprogress: [], deployment: [], readyforqa: [], qa: [], review: [], done: [], reopened: [], cancelled: [] };
  taskList.forEach((task) => {
    const col = STATUS_TO_COL[task.status] ?? 'todo';
    grouped[col].push(normaliseTask(task));
  });
  return COLUMNS.map((c) => ({ ...c, tasks: grouped[c.id], count: grouped[c.id].length }));
};

// ─── Small presentational components ──────────────────────────────────────────

const PriorityFlag = ({ priority }) => (
  <span className={`${PRIORITY_COLORS[priority] ?? 'text-gray-400'} text-xl leading-none`}>⚑</span>
);

const TaskTypeIcon = ({ type }) => {
  const cfg = TASK_ICON_CFG[type];
  if (!cfg) return null;
  return (
    <div className={`w-4 h-4 rounded ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
      <svg className="w-3 h-3 text-white" viewBox="0 0 16 16" fill="currentColor">
        {cfg.type === 'circle' && <circle cx="8" cy="8" r="6" />}
        {cfg.type === 'rect'   && <rect x="4" y="4" width="8" height="8" />}
        {cfg.type === 'path'   && <path d={cfg.d} />}
      </svg>
    </div>
  );
};

const Avatar = ({ empId, emp, size = 'w-7 h-7', className = '' }) => (
  <div
    className={`${size} ${getAvatarColor(empId)} rounded-full flex items-center justify-center
      text-white text-xs font-bold shadow-sm flex-shrink-0 ${className}`}
    title={emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown'}
  >
    {getInitials(emp)}
  </div>
);

// ─── ScrollArrows ─────────────────────────────────────────────────────────────
// Floating buttons that scroll a horizontally-overflowing board left/right.
// Fixed to the viewport so they stay reachable no matter how tall the board is.

const ScrollArrows = ({ containerRef }) => {
  const [canLeft, setCanLeft]   = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, [containerRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    update();

    el.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    const ro = new ResizeObserver(update);
    ro.observe(el);
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
      mo.disconnect();
    };
  }, [containerRef, update]);

  const scroll = (dir) => containerRef.current?.scrollBy({ left: dir * 360, behavior: 'smooth' });

  if (!canLeft && !canRight) return null;

  return (
    <>
      {canLeft && (
        <button
          onClick={() => scroll(-1)}
          aria-label="Scroll left"
          className="fixed bottom-24 right-20 z-[60] w-10 h-10 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:text-blue-600 hover:shadow-xl transition-all"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canRight && (
        <button
          onClick={() => scroll(1)}
          aria-label="Scroll right"
          className="fixed bottom-24 right-6 z-[60] w-10 h-10 rounded-full bg-white shadow-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:text-blue-600 hover:shadow-xl transition-all"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </>
  );
};

// ─── TaskCard ─────────────────────────────────────────────────────────────────
// Defined at module level and memoized so it never re-mounts during board renders.

const TaskCard = React.memo(({
  task, columnId, taskIndex, colIndex,
  hoveredCard, setHoveredCard,
  onDragStart, onDragEnd,
  onToggleComplete, onOpenModal,
  employees,
}) => {
  const emp = employees.find((e) => e.empId === task.user_id);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task, columnId)}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setHoveredCard(task.id)}
      onMouseLeave={() => setHoveredCard(null)}
      onClick={() => onOpenModal(task)}
      className={`
        task-card
        bg-white rounded-lg p-3 shadow-sm border border-slate-200/50
        cursor-grab active:cursor-grabbing select-none
        transition-shadow transition-transform duration-200
        hover:shadow-md hover:scale-[1.02] hover:-translate-y-1
        ${hoveredCard === task.id ? 'ring-2 ring-blue-400 ring-opacity-50' : ''}
        ${task.completed ? 'opacity-60' : ''}
      `}
    >
      {/* Row 1 – checkbox, type icon, task code, priority */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleComplete(columnId, task.id); }}
            className={`
              flex-shrink-0 w-5 h-5 rounded border-2 transition-all duration-200
              flex items-center justify-center
              ${task.completed
                ? 'bg-green-500 border-green-500 hover:bg-green-600'
                : 'border-slate-300 hover:border-green-500 hover:bg-green-50'}
            `}
          >
            {task.completed && <Check className="w-3.5 h-3.5 text-white stroke-[3]" />}
          </button>

          <TaskTypeIcon type={task.type} />

          <span className={`text-xs font-medium truncate ${task.completed ? 'line-through text-slate-400' : 'text-slate-500'}`}>
            {task.project_code ?? 'N/A'}
          </span>
        </div>

        <PriorityFlag priority={task.priority} />
      </div>

      {/* Title */}
      <p className={`text-sm font-medium mb-3 line-clamp-2 ${task.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
        {task.title}
      </p>

      {/* Row 3 – project name + assignee avatar */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-500 truncate flex-1 mr-2">
          {task.project_name}
        </p>
        <Avatar empId={task.user_id} emp={emp} />
      </div>
    </div>
  );
});

// ─── DropZone ─────────────────────────────────────────────────────────────────

// realColId = the true COLUMNS id (e.g. "todo") used for status mapping + card columnId
// col.id    = may be composite like "123-todo" in the employee view (used only for hover highlight)
const DropZone = ({
  col, realColId, tasks, colIndex,
  draggedOverId, onDragOver, onDragEnter, onDragLeave, onDrop,
  cardProps,
  fixedHeight,
}) => {
  const colIdForDrop = realColId ?? col.id;
  return (
    <div
      className={`
        ${col.bgColor} rounded-xl p-3 border border-slate-200/50
        transition-all duration-200 space-y-3
        ${fixedHeight ? 'overflow-y-auto' : ''}
        ${draggedOverId === col.id ? 'ring-2 ring-blue-400 ring-opacity-50 scale-[1.01]' : ''}
      `}
      style={
        fixedHeight
          ? { height: '320px' }
          : { minHeight: 'var(--board-col-height, 500px)' }   // ← synced height
      }
      onDragOver={onDragOver}
      onDragEnter={() => onDragEnter(col.id)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, colIdForDrop)}
    >
      {tasks.length === 0 ? (
        <div className="flex items-center justify-center h-16 text-xs text-slate-300 select-none">
          No tasks
        </div>
      ) : (
        tasks.map((task, taskIndex) => (
          <TaskCard
            key={task.id}
            task={task}
            columnId={colIdForDrop}
            taskIndex={taskIndex}
            colIndex={colIndex}
            {...cardProps}
          />
        ))
      )}
    </div>
  );
};

// ─── KanbanView (top-level so React never remounts it on parent re-render) ─────

const KanbanView = ({ columns, filterTasks, dropZoneProps }) => {
  const containerRef = React.useRef(null);

  // After each render, measure the tallest column and set the CSS variable
  React.useEffect(() => {
    if (!containerRef.current) return;
    const colEls = containerRef.current.querySelectorAll('.drop-zone-col');
    let max = 500;
    colEls.forEach((el) => {
      // Temporarily remove minHeight so we measure natural content height
      el.style.minHeight = '0px';
      max = Math.max(max, el.scrollHeight);
    });
    containerRef.current.style.setProperty('--board-col-height', `${max}px`);
    // Re-apply the variable (the style binding in DropZone reads it)
    colEls.forEach((el) => {
      el.style.minHeight = '';
    });
  });

  return (
    <div className="p-6 overflow-x-auto kanban-scroll-area" ref={containerRef}>
      <div className="flex gap-4 min-w-max pb-6">
        {columns.map((col, colIndex) => {
          const colTasks = filterTasks(col.tasks);
          const points = sumPoints(colTasks);
          return (
            <div key={col.id} className="flex-shrink-0 w-72 drop-zone-col">
             <div className="mb-3 flex items-center justify-between">
  <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
    {col.title}
  </h3>
  <span className="text-[11px] font-semibold text-slate-400 bg-white px-2 py-0.5 rounded-full border border-slate-200">
    {colTasks.length} {colTasks.length === 1 ? 'task' : 'tasks'}
  </span>
</div>
              <DropZone
                col={col}
                tasks={colTasks}
                colIndex={colIndex}
                {...dropZoneProps}
              />
            </div>
          );
        })}
      </div>
      <ScrollArrows containerRef={containerRef} />
    </div>
  );
};

// ─── EmployeeSection (top-level, memoized) ────────────────────────────────────

const EmployeeSection = React.memo(({
  empId, label, avatarNode,
  columns, filterTasks, dropZoneProps,
  expandedEmployees, setExpandedEmployees,
}) => {
  const isOpen = expandedEmployees[empId] ?? true;

  const byCol = columns.reduce((acc, col) => {
    acc[col.id] = filterTasks(
      (col.tasks ?? []).filter((t) =>
        empId === 'unassigned' ? !t.user_id : t.user_id === empId
      )
    );
    return acc;
  }, {});
  const empTasks   = Object.values(byCol).flat();
  const doneCount  = byCol.done?.length ?? 0;
  const totalCount = empTasks.length;
  const points     = sumPoints(empTasks);
  const progress   = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <div className="contents">
      {/* Employee header row spanning all columns */}
      <div
        className="col-span-full flex items-center gap-3 px-4 py-2.5 bg-white border border-slate-200/60 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpandedEmployees((prev) => ({ ...prev, [empId]: !isOpen }))}
      >
        {isOpen
          ? <ChevronDown  className="w-4 h-4 text-slate-400 flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        }
        {avatarNode}
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className="ml-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full font-medium">
          {totalCount} task{totalCount !== 1 ? 's' : ''}
        </span>

        {points > 0 && (
          <span className="px-2 py-0.5 bg-violet-50 text-violet-500 text-xs rounded-full font-medium">
            {points} pts
          </span>
        )}

        {totalCount > 0 && (
          <div className="ml-auto flex items-center gap-2 w-40 flex-shrink-0">
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${progress}%`, background: 'linear-gradient(135deg, #2563eb, #d12465)' }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-400 w-10 text-right">{progress}%</span>
          </div>
        )}
      </div>

      {/* Task columns — only rendered when expanded */}
      {isOpen && COLUMNS.map((col, colIndex) => (
        <div key={col.id} className="flex-shrink-0">
          <DropZone
            col={{ ...col, id: `${empId}-${col.id}` }}
            realColId={col.id}
            tasks={byCol[col.id] ?? []}
            colIndex={colIndex}
            fixedHeight
            {...dropZoneProps}
          />
        </div>
      ))}
    </div>
  );
});

// ─── EmployeeGroupedView (top-level so React never remounts it on parent re-render) ─

const EmployeeGroupedView = ({
  allTasks, columns, employees, filterTasks, dropZoneProps,
  expandedEmployees, setExpandedEmployees,
}) => {
  const containerRef  = React.useRef(null);
  const empIds        = [...new Set(allTasks.map((t) => t.user_id).filter(Boolean))];
  const hasUnassigned = allTasks.some((t) => !t.user_id);

  const sectionProps = { columns, filterTasks, dropZoneProps, expandedEmployees, setExpandedEmployees };

  // Grid: 1 col for the employee header (col-span-full), then 6 cols for kanban columns
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${COLUMNS.length}, 17rem)`,
    gap: '1rem',
    alignItems: 'start',
  };

  return (
    <div className="p-6 overflow-x-auto kanban-scroll-area" ref={containerRef}>
      <div style={{ minWidth: 'max-content' }}>

      {/* ── Sticky column headers ── */}
      <div
        className="sticky top-0 z-10 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-2"
        style={gridStyle}
      >
        {COLUMNS.map((col) => {
          // Sum up filtered tasks for this column across ALL employees
          const colCount = filterTasks(
            allTasks.filter((t) => (STATUS_TO_COL[t.status] ?? 'todo') === col.id)
          ).length;

          return (
            <div key={col.id} className="flex items-center gap-2 px-1 h-8">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                {col.title}
              </span>
              <span className="text-xs font-normal text-slate-400">
                {colCount}
              </span>
            </div>
          );
        })}
      </div>

        {/* ── Employee rows ── */}
        <div className="space-y-3">
          {empIds.map((empId) => {
            const emp = employees.find((e) => e.empId === empId);
            return (
              <div key={empId} style={gridStyle}>
                <EmployeeSection
                  empId={empId}
                  label={emp ? `${emp.first_name} ${emp.last_name}` : `Employee #${empId}`}
                  avatarNode={<Avatar empId={empId} emp={emp} size="w-8 h-8" />}
                  {...sectionProps}
                />
              </div>
            );
          })}

          {hasUnassigned && (
            <div style={gridStyle}>
              <EmployeeSection
                empId="unassigned"
                label="Sprint"
                avatarNode={
                  <div className="w-8 h-8 bg-slate-300 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">?</div>
                }
                {...sectionProps}
              />
            </div>
          )}
        </div>
      </div>
      <ScrollArrows containerRef={containerRef} />
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const KanbanBoard = () => {
  const { tasks, updateTask } = useTaskContext();
  const { employees }         = useEmployee();

  // View
  const [viewMode, setViewMode]                   = useState('kanban');
  const [expandedEmployees, setExpandedEmployees] = useState({});

  // Board state
  const [columns, setColumns]           = useState(() => COLUMNS.map((c) => ({ ...c, tasks: [], count: 0 })));
  const [hoveredCard, setHoveredCard]   = useState(null);
  const [draggedOverCol, setDraggedOverCol] = useState(null);

  // Track whether a drag is in flight so the tasks useEffect won't stomp our optimistic update
  const isDraggingRef   = useRef(false);
  const draggedCardRef  = useRef(null);   // { task, sourceColumnId }

  // Filters
  const [searchQuery,        setSearchQuery]        = useState('');
  const [selectedProject,    setSelectedProject]    = useState('');
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [showProjectMenu,    setShowProjectMenu]    = useState(false);
  const [showPriorityMenu,   setShowPriorityMenu]   = useState(false);
const [openDropdown, setOpenDropdown] = useState(null);
  // Modal
  const [isModalOpen,  setIsModalOpen]  = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [boardUpdating, setBoardUpdating] = useState(false);

  // ── Derived data ────────────────────────────────────────────────────────────

  // Single source of truth for BOTH views — flat list derived from `columns` state
  // so optimistic drag updates are immediately visible in the employee view too.
  const allTasks = columns.flatMap((c) => c.tasks);

  const assignedProjects = [
    ...new Map(allTasks.map((t) => [t.project_id, { id: t.project_id, name: t.project_name }])).values(),
  ];

  const assignedUserIds = [...new Set(allTasks.map((t) => t.user_id).filter(Boolean))];

  const hasActiveFilters = searchQuery || selectedProject || selectedPriorities.length > 0;

  // ── Populate columns when tasks change (skip while dragging) ────────────────

  useEffect(() => {
    // Don't let context updates stomp the optimistic drag state
    if (isDraggingRef.current) return;

    if (!tasks?.length) {
      setColumns(COLUMNS.map((c) => ({ ...c, tasks: [], count: 0 })));
      return;
    }

    setColumns(buildColumns(tasks));
  }, [tasks]);

  // ── Close dropdowns on outside click ──────────────────────────────────────

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.filter-dropdown')) {
        setShowProjectMenu(false);
        setShowPriorityMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Filtering ──────────────────────────────────────────────────────────────

 const filterTasks = useCallback(
  (taskList) =>
    taskList.filter((task) => {
      const search = searchQuery?.toLowerCase();

      const formattedTaskId =
        `${task.type || ""}-${task.project_code || ""}`.toLowerCase();

      if (
        search &&
        !task.title?.toLowerCase().includes(search) &&
        !task.id?.toString().toLowerCase().includes(search) &&
        !formattedTaskId.includes(search)
      ) {
        return false;
      }

      if (
        selectedPriorities.length &&
        !selectedPriorities.includes(task.priority)
      )
        return false;

      if (
        selectedProject &&
        task.project_id?.toString() !== selectedProject
      )
        return false;

      return true;
    }),
  [searchQuery, selectedPriorities, selectedProject]
);

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedProject('');
    setSelectedPriorities([]);
  };

  // ── Drag-and-drop ──────────────────────────────────────────────────────────

  const handleDragStart = useCallback((e, task, sourceColumnId) => {
    isDraggingRef.current  = true;
    draggedCardRef.current = { task, sourceColumnId };
    e.dataTransfer.effectAllowed = 'move';
    // Capture the DOM node NOW — e.currentTarget is nulled by React after the
    // event handler returns, so we must not reference it inside rAF/setTimeout.
    const node = e.currentTarget;
    requestAnimationFrame(() => {
      if (node) node.style.opacity = '0.5';
    });
  }, []);

  const handleDragEnd = useCallback((e) => {
    // Capture node synchronously before React clears currentTarget
    const node = e.currentTarget;
    if (node) node.style.opacity = '1';
    // Give handleDrop a tick to fire first, then release the drag lock
    setTimeout(() => {
      isDraggingRef.current  = false;
      draggedCardRef.current = null;
      setDraggedOverCol(null);
    }, 0);
  }, []);

  const handleDragOver  = useCallback((e) => e.preventDefault(), []);
  const handleDragEnter = useCallback((colId) => setDraggedOverCol(colId), []);

  const handleDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDraggedOverCol(null);
  }, []);

const handleDrop = useCallback(async (e, targetColumnId) => {
  e.preventDefault();
  setDraggedOverCol(null);

  const dragged = draggedCardRef.current;
  if (!dragged) return;

  const { task, sourceColumnId } = dragged;

  if (sourceColumnId === targetColumnId) return;

  // ✅ Validation for Est. Hours and Points
  const isHoursMissing =
    task.time === null ||
    task.time === undefined ||
    task.time === '';

  const isPointsMissing =
    task.points === null ||
    task.points === undefined ||
    task.points === '';

  if (isHoursMissing || isPointsMissing) {

    let message = '';

    if (isHoursMissing && isPointsMissing) {
      message = 'Est. Hours and Points are mandatory before changing task status.';
    } else if (isHoursMissing) {
      message = 'Est. Hours is mandatory before changing task status.';
    } else if (isPointsMissing) {
      message = 'Points is mandatory before changing task status.';
    }

    alert(message);

    isDraggingRef.current = false;
    draggedCardRef.current = null;

    return;
  }

  const newStatus = COL_TO_STATUS[targetColumnId];

  // Start loading overlay
  setBoardUpdating(true);

  // Optimistic UI update
  setColumns((prev) =>
    prev.map((col) => {
      if (col.id === sourceColumnId) {
        const updated = col.tasks.filter((t) => t.id !== task.id);
        return { ...col, tasks: updated, count: updated.length };
      }

      if (col.id === targetColumnId) {
        const updated = [{ ...task, status: newStatus }, ...col.tasks];
        return { ...col, tasks: updated, count: updated.length };
      }

      return col;
    })
  );

  try {

    const response = await updateTask(task.id, { status: newStatus });

    const updatedTask = response.task;

    setColumns((prev) =>
      prev.map((col) => {

        if (col.id === sourceColumnId) {
          const updatedTasks = col.tasks.filter((t) => t.id !== task.id);

          return {
            ...col,
            tasks: updatedTasks,
            count: updatedTasks.length,
          };
        }

        if (col.id === targetColumnId) {

          const updatedTasks = [
            {
              ...task,
              ...updatedTask,
            },
            ...col.tasks.filter((t) => t.id !== task.id),
          ];

          return {
            ...col,
            tasks: updatedTasks,
            count: updatedTasks.length,
          };
        }

        return col;
      })
    );

  } catch (err) {

    console.error('Failed to update task status:', err);

    // Rollback
    setColumns(buildColumns(tasks));

  } finally {

    setBoardUpdating(false);

    isDraggingRef.current = false;
    draggedCardRef.current = null;
  }

}, [updateTask, tasks]);

  // ── Toggle task complete (local only) ─────────────────────────────────────

  const toggleTaskCompletion = useCallback((columnId, taskId) =>
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId
          ? { ...col, tasks: col.tasks.map((t) => (t.id === taskId ? { ...t, completed: !t.completed } : t)) }
          : col
      )
    ), []);

  // ── Open modal ─────────────────────────────────────────────────────────────

  const openTaskModal = useCallback((task) => {
    const emp  = employees.find((e) => e.empId === task.user_id);
    const name = emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
    const init = emp ? `${emp.first_name[0]}${emp.last_name[0]}` : 'U';

    setSelectedTask({
      id:            task.id,
      task_id:       task.task_id,
      project_code:  task.project_code,
      type:          task.type,
      title:         task.title,
      description:   task.description,
      comments:      task.comments,
      status:        task.status,
      startDate:     task.start_date,
      DeploymentDate: task.deployment_date,
      endDate:       task.end_date,
      timeEstimate:  task.time,
      completedTime: task.completed_hours,
      message:       task.message,
      links:         task.links  ?? [],
      images:        task.images ?? [],
      assignees:     task.user_id
        ? [{ id: task.user_id, name, initials: init, color: 'bg-blue-400' }]
        : [],
      priority:      task.priority?.toLowerCase() ?? '',
      sprintPoints:  task.points,
      tags:          Array.isArray(task.type)
        ? task.type.map((tag) => ({ id: Math.random().toString(), name: tag, color: 'bg-blue-500' }))
        : [],
      projectId:     task.project_id,
    });
    setIsModalOpen(true);
  }, [employees]);

  // ── Stable shared card props (memoized to avoid re-creating on each render) ─

  const sharedCardProps = {
    hoveredCard,
    setHoveredCard,
    onDragStart:      handleDragStart,
    onDragEnd:        handleDragEnd,
    onToggleComplete: toggleTaskCompletion,
    onOpenModal:      openTaskModal,
    employees,
  };

  const dropZoneProps = {
    draggedOverId: draggedOverCol,
    onDragOver:    handleDragOver,
    onDragEnter:   handleDragEnter,
    onDragLeave:   handleDragLeave,
    onDrop:        handleDrop,
    cardProps:     sharedCardProps,
  };

  // ── Sub-views ──────────────────────────────────────────────────────────────

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`
    min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 font-sans
    transition-opacity duration-300
    ${boardUpdating ? 'opacity-70 pointer-events-none' : 'opacity-100'}
  `} >

      {/* ── Toolbar ── */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 flex-wrap">

            {/* Search */}
          <div className="relative">
  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
  <input
    type="text"
    placeholder="Search"
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    className="pl-10 pr-4 py-2 border border-gray-200 bg-gray-50 rounded-lg outline-none w-64 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
  />
</div>

            {/* Team avatars */}
            <div className="flex -space-x-2">
              {assignedUserIds.slice(0, 5).map((userId) => {
                const emp = employees.find((e) => e.empId === userId);
                if (!emp) return null;
                return (
                  <Avatar
                    key={userId}
                    empId={userId}
                    emp={emp}
                    size="w-8 h-8"
                    className="border-2 border-white hover:scale-110 transition-transform cursor-pointer"
                  />
                );
              })}
              {assignedUserIds.length > 5 && (
                <div className="w-8 h-8 bg-slate-200 rounded-full border-2 border-white flex items-center justify-center text-slate-600 text-xs font-medium">
                  +{assignedUserIds.length - 5}
                </div>
              )}
            </div>

            {/* Project filter */}
           {/* Project filter */}
<div className="relative filter-dropdown">
  <button
    onClick={() => setOpenDropdown(prev => prev === 'project' ? null : 'project')}
    className="px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-2 text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
  >
    Project
    {selectedProject && (
      <span className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">1</span>
    )}
    <ChevronDown className="w-4 h-4" />
  </button>

  {openDropdown === 'project' && (
    <div className="absolute top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 w-48 max-h-64 overflow-y-auto">
      <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
        <input
          type="radio" name="projectFilter" value=""
          checked={selectedProject === ''}
          onChange={() => setSelectedProject('')}
        />
        <span className="text-sm">All Projects</span>
      </label>
      {assignedProjects.map((project) => (
        <label key={project.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
          <input
            type="radio" name="projectFilter" value={project.id}
            checked={selectedProject === project.id.toString()}
            onChange={() => setSelectedProject(project.id.toString())}
          />
          <span className="text-sm">{project.name}</span>
        </label>
      ))}
    </div>
  )}
</div>

            {/* Priority quick-filters */}
          {/* Priority quick-filters */}
<div className="relative filter-dropdown">
  <button
    onClick={() => setOpenDropdown(prev => prev === 'priority' ? null : 'priority')}
    className="px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-2 text-sm font-medium text-slate-700 outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] focus:bg-white transition"
  >
    Quick filters
    {selectedPriorities.length > 0 && (
      <span className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">
        {selectedPriorities.length}
      </span>
    )}
    <ChevronDown className="w-4 h-4" />
  </button>

  {openDropdown === 'priority' && (
    <div className="absolute top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-50 w-48">
      <div className="px-3 py-1 text-xs font-semibold text-slate-500 uppercase">Priority</div>
      {['high', 'medium', 'low', 'urgent'].map((priority) => (
        <label key={priority} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
          <input
            type="checkbox"
            checked={selectedPriorities.includes(priority)}
            onChange={() =>
              setSelectedPriorities((prev) =>
                prev.includes(priority) ? prev.filter((p) => p !== priority) : [...prev, priority]
              )
            }
            className="w-4 h-4 text-blue-600 rounded"
          />
          <span className="text-sm capitalize">{priority}</span>
        </label>
      ))}
    </div>
  )}
</div>

            {/* Clear filters */}
            <button
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
              className={`px-3 py-2 hover:bg-slate-50 rounded-lg flex items-center gap-2 text-sm font-medium
                ${hasActiveFilters ? 'text-slate-700' : 'text-slate-300 cursor-not-allowed'}`}
            >
              Clear filters
            </button>

            <div className="flex-1" />

            {/* View toggle */}
            <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
              {[
                { mode: 'kanban',   icon: LayoutGrid, label: 'Board'       },
                { mode: 'employee', icon: Users,      label: 'By Assignee' },
              ].map(({ mode, icon: Icon, label }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all
                    ${viewMode === mode ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/* Add Task */}
            <button
              onClick={() => { setSelectedTask(null); setIsModalOpen(true); }}
              className="px-4 py-2  text-white rounded-lg  transition-all flex items-center gap-2 text-sm font-medium"
          style={{
background: "linear-gradient(135deg, #2563eb, #d12465)"
}}  >
              <Zap className="w-4 h-4" />
              Add Task
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      {viewMode === 'kanban' ? (
        <KanbanView
          columns={columns}
          filterTasks={filterTasks}
          dropZoneProps={dropZoneProps}
        />
      ) : (
        <EmployeeGroupedView
          allTasks={allTasks}
          columns={columns}
          employees={employees}
          filterTasks={filterTasks}
          dropZoneProps={dropZoneProps}
          expandedEmployees={expandedEmployees}
          setExpandedEmployees={setExpandedEmployees}
        />
      )}

      {/* ── Modal ── */}
      <TaskModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedTask(null); }}
        taskData={selectedTask}
      />

{boardUpdating && (
  <div className="fixed bottom-5 right-5 z-[9999]">
    <div className="bg-slate-900 text-white px-4 py-2 rounded-xl shadow-2xl flex items-center gap-3 animate-pulse">
      
      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>

      <span className="text-sm font-medium">
        Updating task...
      </span>
    </div>
  </div>
)}

      <style>{`
        .line-clamp-2 { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
        .kanban-scroll-area { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
        .kanban-scroll-area::-webkit-scrollbar { height: 10px; }
        .kanban-scroll-area::-webkit-scrollbar-track { background: transparent; }
        .kanban-scroll-area::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
        .kanban-scroll-area::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
};

export default KanbanBoard;